/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction } from '../../../helpers/jsHelpers/types';
import type { ModalState } from '../../../redux/modal/reducers';
import type { Dispatch } from '../../../redux/types/types';
import type { PIN_DATA_MODAL_TYPE } from '../pinModal/PinModalConstsAndTypes';

import { fireAndForget } from '../../../helpers/jsHelpers/promise';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import ParentalControlManager from '../../../helpers/parentalControl/parentalControlManager';
import { isProfilePrivate } from '../../../helpers/profiles/helpers';
import ProfilesManager from '../../../helpers/profiles/profilesManager';
import { MAX_PROFILE_COUNT, type Profile, type ProfileList } from '../../../helpers/profiles/typesAndConstants';
import { showParentalControlModal, showPinModal, showProfileEditorModal } from '../../../redux/modal/actions';
import InfiniteCircleLoaderArc from '../../loader/infiniteCircleLoaderArc';
import { PictoPlus } from '../../pictos/Element';
import Modal, { ModalView } from '../modal';
import { PinModalKind } from '../pinModal/PinModalConstsAndTypes';
import ProfileView from './Profile';
import './ProfilesModal.css';

export type ReduxProfilesModalDispatchToPropsType = {
  +localShowParentalControlModal: BasicFunction,
  +localShowPinModal: (data: PIN_DATA_MODAL_TYPE, modalId?: string) => void,
  +localShowProfileEditorModal: (modalId: string, profileId?: string) => void,
};

export type ReduxProfilesModalReducerStateType = {};

export type CompleteProfilesModalPropType = {
  ...ModalState,
  ...ReduxProfilesModalDispatchToPropsType,
  ...ReduxProfilesModalReducerStateType,
};

export type ProfilesModalStateType = {
  currentProfile: Profile | null,
  isEditMode: boolean,
  initialId: string | null,
  isLoading: boolean,
  profiles: ProfileList,
};

class ProfilesModalView extends React.PureComponent<CompleteProfilesModalPropType, ProfilesModalStateType> {
  abortController: AbortController;
  modal: ModalView | null;

  constructor(props: CompleteProfilesModalPropType) {
    super(props);

    this.state = { currentProfile: null, initialId: null, isEditMode: false, isLoading: true, profiles: [] };

    this.abortController = new AbortController();
  }

  componentDidMount() {
    Messenger.on(MessengerEvents.PROFILES_UPDATE, this.update);
    fireAndForget(this.update(), 'ProfilesModalView.update');
  }

  componentDidUpdate(prevProps: CompleteProfilesModalPropType, prevState: ProfilesModalStateType) {
    const { profiles } = this.state;
    const { profiles: prevProfiles } = prevState;

    if (profiles !== prevProfiles) {
      this.setCurrentProfile();
    }
  }

  componentWillUnmount() {
    const { abortController } = this;

    abortController.abort('Component ProfilesModal will unmount');
    Messenger.off(MessengerEvents.PROFILES_UPDATE, this.update);
  }

  update = async () => {
    const profiles = await ProfilesManager.getProfiles(true);
    // Copy array to force state change (and re-render)
    this.setState({ profiles: [...profiles.list] });
  };

  setCurrentProfile = () => {
    const currentProfile = ProfilesManager.getCurrentProfile();

    this.setState({ currentProfile, initialId: currentProfile?.id ?? null, isLoading: false });
  };

  editProfile = (profileId: string) => {
    const { localShowProfileEditorModal } = this.props;

    const modalId = crypto.randomUUID();
    Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${modalId}`, this.update);
    localShowProfileEditorModal(modalId, profileId);
  };

  handleProfileOnClick = async (profileId: string) => {
    const { localShowPinModal } = this.props;
    const { initialId, isEditMode } = this.state;

    if (isProfilePrivate(ProfilesManager.getProfile(profileId)) && profileId !== initialId) {
      // Private profile: prompt user for their PIN
      const modalId = crypto.randomUUID();
      Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${modalId}`, async (isvalidPin: boolean) => {
        if (isvalidPin) {
          if (isEditMode) {
            this.editProfile(profileId);
          } else {
            await this.selectProfile(profileId);
          }
        }
      });
      localShowPinModal({ kind: PinModalKind.UnlockProfile, profileId }, modalId);
    } else {
      // Public profiles: select or edit immediately
      if (isEditMode) {
        this.editProfile(profileId);
      } else {
        await this.selectProfile(profileId);
      }
    }
  };

  selectProfile = async (id: string) => {
    const { initialId } = this.state;

    ProfilesManager.setCurrentProfile(id);
    await this.update();

    // Send boolean telling if profile switched
    this.modal?.close(id !== initialId);

    Messenger.emit(MessengerEvents.REFRESH_PERSONAL_DATA);
  };

  createProfile = () => {
    const { localShowProfileEditorModal } = this.props;

    const modalId = crypto.randomUUID();
    Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${modalId}`, this.update);
    localShowProfileEditorModal(modalId);
  };

  handleCreateProfile = () => {
    const { localShowPinModal } = this.props;

    if (ProfilesManager.isProfileManagementLocked) {
      // Profile creation is restricted: Master PIN is required
      const modalId = crypto.randomUUID();
      Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${modalId}`, (isvalidPin: boolean) => {
        if (isvalidPin) {
          this.createProfile();
        }
      });

      localShowPinModal({ kind: PinModalKind.CheckMaster }, modalId);
    } else {
      // Profile creation is accessible to anyone
      this.createProfile();
    }
  };

  handleEditProfilesOnClick = () => {
    this.setState((prevState) => {
      return { isEditMode: !prevState.isEditMode };
    });
  };

  handleParentalControlOnClick = () => {
    const { localShowPinModal } = this.props;

    if (ParentalControlManager.hasPinCode()) {
      // Parental control management
      const modalId = crypto.randomUUID();
      Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${modalId}`, this.parentalControlSettingsUnlockClosedCallback);
      localShowPinModal({ kind: PinModalKind.UnlockSettings }, modalId);
    } else {
      // PIN code creation
      localShowPinModal({ kind: PinModalKind.CreateMaster });
    }
  };

  parentalControlSettingsUnlockClosedCallback = (isValidPin: boolean): void => {
    const { localShowParentalControlModal } = this.props;

    if (!isValidPin) {
      return;
    }

    localShowParentalControlModal();
  };

  renderContent = (): React.Node => {
    const { currentProfile, isEditMode, profiles } = this.state;

    const isCreateAllowed = profiles.length < MAX_PROFILE_COUNT;

    return (
      <div className='profilesContent'>
        <div className='profilesHeader'>{Localizer.localize('profiles.who_watching')}</div>
        <div className='profilesList'>
          {profiles.map((p) => (
            <ProfileView isEditMode={isEditMode} isSelected={p.id === currentProfile?.id} key={p.id} onClick={this.handleProfileOnClick} profile={p} />
          ))}
          {isCreateAllowed ? (
            <button aria-label={Localizer.localize('accessibility.profiles.create_profile')} className='createBtn' onClick={this.handleCreateProfile} type='button'>
              <PictoPlus forceHoverEffect />
            </button>
          ) : null}
        </div>
        <div className='profilesButtons'>
          <button
            aria-label={Localizer.localize(isEditMode ? 'accessibility.profiles.finish_modification' : 'accessibility.profiles.modify_profiles')}
            className='elongatedBtn'
            onClick={this.handleEditProfilesOnClick}
            type='button'
          >
            {Localizer.localize(isEditMode ? 'profiles.actions.finish' : 'profiles.actions.modify_profiles')}
          </button>
          <button aria-label={Localizer.localize('accessibility.profiles.parental_control')} className='elongatedBtn' onClick={this.handleParentalControlOnClick} type='button'>
            {Localizer.localize('settings.parental_control.title')}
          </button>
        </div>
      </div>
    );
  };

  setModalRef = (instance: ModalView | null) => {
    this.modal = instance;
  };

  render(): React.Node {
    const { isLoading } = this.state;

    return (
      <Modal
        ariaLabel={Localizer.localize('accessibility.modal.profiles')}
        canCloseOnEscape={false}
        canCloseOnOverlayClick={false}
        className='profiles'
        hasBackgroundImage
        hasCloseButton={false}
        innerRef={this.setModalRef}
      >
        {isLoading ? (
          <div className='loader'>
            <InfiniteCircleLoaderArc />
          </div>
        ) : (
          this.renderContent()
        )}
      </Modal>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch): ReduxProfilesModalDispatchToPropsType => {
  return {
    localShowParentalControlModal: (): void => dispatch(showParentalControlModal()),
    localShowPinModal: (data: PIN_DATA_MODAL_TYPE, modalId?: string): void => dispatch(showPinModal(data, modalId)),
    localShowProfileEditorModal: (modalId: string, profileId?: string): void => dispatch(showProfileEditorModal(modalId, profileId)),
  };
};

const ProfilesModal: React.ComponentType<ModalState> = connect(null, mapDispatchToProps)(ProfilesModalView);

export default ProfilesModal;
